import { useTranslation } from "react-i18next";
import { ExternalLink, Plane } from "lucide-react";
import ExpressFullIcon from "@/assets/icons/express-full.svg?react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import Paths from "@/constants/paths";

type IconBadgeProps = {
  icon: React.ReactNode;
  label: string;
  className?: string;
};

const IconBadge = ({ icon, label, className }: IconBadgeProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-lg border",
          className,
        )}
      >
        {icon}
      </span>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

export const ExpeditionAttributeBadges = ({
  expedition,
}: {
  expedition: GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const isExpress = expedition.expeditionOptions?.express;
  const isRemote =
    !expedition.pickupInfo?.pickupLocation?.pickupZones ||
    expedition.pickupInfo?.pickupLocation?.pickupZones?.remote;
  const hasFedexApi = !!expedition.expeditionFedexApi;

  return (
    <div className="flex flex-row gap-1">
      {isExpress && (
        <IconBadge
          icon={<ExpressFullIcon className="size-5" />}
          label={t("expeditionDrawer.expressDelivery")}
          className="bg-[#3562f8]/10 text-[#3562f8]"
        />
      )}
      {isRemote && (
        <IconBadge
          icon={<Plane className="size-4" fill="currentColor" stroke="none" />}
          label={t("expeditionDrawer.remoteDelivery")}
          className="text-foreground bg-gray-100"
        />
      )}
      {!hasFedexApi ? (
        <span className="text-foreground flex h-7 shrink-0 items-center rounded-lg border bg-gray-100 px-2 text-xs font-medium">
          {t("expeditionDrawer.outsideApi")}
        </span>
      ) : (
        <span className="flex h-7 shrink-0 items-center rounded-lg border bg-[#3562f8]/10 px-2 text-xs font-medium text-[#3562f8]">
          {t("expeditionDrawer.viaApi")}
        </span>
      )}
    </div>
  );
};

export const TrackingAlert = ({ expedition }: { expedition: GetOneExpeditionBoResponseDto }) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const tracking = expedition.tracking;
  if (!tracking) return null;

  const label = tracking.manualTracking
    ? t("expeditionDrawer.trackingLink")
    : tracking.trackingCode;
  const link = tracking.manualTracking
    ? tracking.trackingUrl
    : `${Paths.others.tracking}?code=${expedition.id}`;

  if (!label || !link) return null;

  return (
    <button
      type="button"
      onClick={() => window.open(link, "_blank")}
      className="flex flex-row items-start gap-4 rounded-lg border border-blue-500 bg-blue-50 px-2 py-1 text-left transition-colors hover:bg-blue-100"
    >
      <p className="text-[.7rem] font-semibold text-blue-700 uppercase">
        {t("expeditionDrawer.trackingNumber")}
      </p>
      <div className="flex flex-row items-center gap-1">
        <p className="text-md font-bold text-blue-700">{label}</p>
        <ExternalLink className="size-3 text-blue-700" />
      </div>
    </button>
  );
};
